import DeviceCardImage from "@common/components/ListDetailViews/DetailView/DeviceCard/DeviceCardImage";
import { useErrorNotification } from "@common/components/Notification";
import {
  Add,
  Close,
  Edit,
  FiberManualRecord,
  Groups,
  Place,
  Remove,
  Star,
  StarBorder,
  Videocam,
  Warning,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import { CustomRoom, Protocol } from "@services/meetings/types";
import { Room } from "@services/rooms/types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { room_logo } from "@assets/images";
import { IconButton, LinearProgress } from "@mui/material";
import { AddParticipantContext } from "..";
import {
  addCustomRoomToFavorites,
  addRoomToFavorites,
  removeCustomRoomFromFavorites,
  removeRoomFromFavorites,
  updateCustomFavorite,
} from "@services/meetings";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { Room as RoomIcon, VIP } from "@assets/icons";
import Typography from "@common/components/Typography";
import { useSimpleReducer } from "@common/hooks";
import CustomRoomForm from "../CustomRoomForm";
import Button from "@common/components/Button";
import { Ticket } from "@services/tickets/types";
import { getTickets } from "@services/tickets";
import TicketCard from "../TicketCard";
import { ButtonControl } from "@common/components/ExtendedProperties/components/ExtendedProperty/Controls/controls";

type RoomCardProps = {
  room?: Room;
  customRoom?: CustomRoom;
};

const InformationLabel = (props: {
  icon: React.JSX.Element;
  label?: string | number;
}) => (
  <Grid
    container
    wrap="nowrap"
    sx={{ width: "100%", alignItems: "center", gap: "0.25rem" }}
  >
    {props.icon}

    <Typography
      size="small"
      tooltip={String(props.label)}
      sx={{ overflowWrap: "anywhere" }}
    >
      {props.label}
    </Typography>
  </Grid>
);

const RoomCard = (props: RoomCardProps) => {
  const { room, customRoom } = props;
  const { favorites, selectedRooms, dispatch, fetchData } = React.useContext(
    AddParticipantContext
  );
  const [user] = useAuthenticatedUser();
  const dispatchError = useErrorNotification();
  const { t } = useTranslation();
  const [loadFavorite, setLoadFavorite] = useState<boolean>(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [showAllTickets, setShowAllTickets] = useState<boolean>(false);
  const [
    { name, protocol, address, extension, saving, editMode, showTickets },
    setForm,
  ] = useSimpleReducer<{
    showTickets: boolean;
    editMode: boolean;
    saving: boolean;
    name: string;
    protocol: Protocol;
    address: string;
    extension: string;
  }>({
    showTickets: false,
    editMode: false,
    saving: false,
    name: customRoom?.name ?? "",
    protocol: customRoom?.protocol ?? Protocol.H323,
    address: customRoom?.address ?? "",
    extension: customRoom?.extension ?? "",
  });

  const selected = selectedRooms.some((r) =>
    Boolean(customRoom) ? r.id === customRoom?.id : r.id === room?.id
  );
  const favorite =
    favorites.internal.some((r) => r.id === room?.id) ||
    favorites.external.some(
      (r) =>
        r.name === customRoom?.name &&
        r.address === customRoom?.address &&
        r.protocol === customRoom?.protocol
    );

  const getCustomFavoriteId = () => {
    return favorites.external.find(
      (r) =>
        r.name === customRoom?.name &&
        r.address === customRoom?.address &&
        r.protocol === customRoom?.protocol
    )?.id;
  };

  const handleOnClickFavorite = async () => {
    setLoadFavorite(true);
    try {
      if (room !== undefined) {
        if (favorite) await removeRoomFromFavorites(user?.id, room.id);
        else await addRoomToFavorites(user?.id, room.id);
      }
      if (customRoom !== undefined) {
        if (favorite)
          await removeCustomRoomFromFavorites(
            customRoom.id ?? getCustomFavoriteId()
          );
        else {
          if (!customRoom.userId) customRoom.userId = user?.id;
          await addCustomRoomToFavorites(customRoom);
        }
      }
      await fetchData();
    } catch (e) {
      dispatchError(e);
    }
    setLoadFavorite(false);
  };

  const handleOnClick = () => {
    if (selected) {
      const index = selectedRooms.indexOf(
        room !== undefined ? room : customRoom ?? {}
      );
      const tmp = selectedRooms;
      selectedRooms.splice(index, 1);
      dispatch({ selectedRooms: tmp });
    } else {
      const tmp = selectedRooms;
      tmp.push(room !== undefined ? room : customRoom ?? {});
      dispatch({ selectedRooms: tmp });
    }
  };

  const handleOnClickSave = async () => {
    if (favorite) {
      setForm({ saving: true });
      try {
        await fetchData();
      } catch (e) {
        dispatchError(e);
      }
      setForm({ saving: false });
    }
    const index = selectedRooms.indexOf(customRoom ?? {});
    let tmp = selectedRooms;
    tmp[index] = {
      ...customRoom,
      name: name ?? customRoom?.name,
      protocol: protocol ?? customRoom?.protocol,
      address: address ?? customRoom?.address,
      extension: extension ?? customRoom?.extension,
    };
    dispatch({ selectedRooms: tmp });
    setForm({ editMode: false });
  };

  const loadTickets = async () => {
    try {
      setTickets(
        (
          await getTickets(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            { "room.id": [room?.id ?? ""], status: ["Open"] }
          )
        ).list
      );
    } catch (error) {
      dispatchError(error);
    }
  };

  useEffect(() => {
    if (showTickets && tickets.length === 0) loadTickets();
  }, [showTickets]);

  const isValidForm =
    name !== "" &&
    protocol !== undefined &&
    address !== "" &&
    (name !== customRoom?.name ||
      protocol !== customRoom?.protocol ||
      address !== customRoom?.address ||
      extension !== customRoom?.extension);

  return (
    <Grid
      container
      direction="row"
      wrap="nowrap"
      sx={{
        gap: 1,
        maxHeight: "470px",
        backgroundColor: ({ palette }) => palette.background.default,
        borderRadius: 1,
      }}
    >
      <Grid container direction="column" wrap="nowrap" sx={{ width: "100%" }}>
        <Grid
          container
          direction="row"
          wrap="nowrap"
          sx={{
            width: "100%",
            minHeight: "80px",
            gap: 1,
            alignItems: "center",
          }}
        >
          <IconButton
            disabled={loadFavorite}
            sx={{ height: "fit-content" }}
            onClick={() => handleOnClickFavorite()}
          >
            {favorite ? <Star /> : <StarBorder />}
          </IconButton>
          <DeviceCardImage
            image={
              room?.logo
                ? `${__symphony_env__.REACT_APP_SERVER_URL}${room.logo}`
                : room_logo
            }
          />
          <Grid
            sx={{
              flexGrow: 1,
              py: "1rem",
            }}
          >
            <Grid
              container
              direction="column"
              sx={{ width: "100%", height: "100%", gap: "0.5rem" }}
            >
              <InformationLabel
                icon={<RoomIcon />}
                label={room !== undefined ? room.name : customRoom?.name}
              />
              {customRoom && (
                <InformationLabel
                  icon={<Place />}
                  label={`${customRoom.protocol}:${customRoom.address}${
                    customRoom.extension ? ":" + customRoom.extension : ""
                  }`}
                />
              )}
              {room && (
                <InformationLabel
                  icon={<Place />}
                  label={`${room?.location?.city}, ${room?.location?.country}`}
                />
              )}
              {selected && room ? (
                (room.devices?.length ?? 0) > 0 ? (
                  room.devices
                    ?.sort((a, b) =>
                      (a.name?.toLowerCase() ?? "").localeCompare(
                        b.name?.toLowerCase() ?? ""
                      )
                    )
                    .map((device) => (
                      <InformationLabel
                        key={device.id}
                        icon={
                          device.type === "Codecs" ? (
                            <Videocam />
                          ) : (
                            <FiberManualRecord />
                          )
                        }
                        label={device.name}
                      />
                    ))
                ) : (
                  <Typography size="small">
                    {t("No endpoint to invite")}
                  </Typography>
                )
              ) : (
                ""
              )}
            </Grid>
          </Grid>
          {customRoom && selected && !editMode && (
            <IconButton
              onClick={() => setForm({ editMode: true })}
              sx={{ color: ({ palette }) => palette.text.primary }}
            >
              <Edit />
            </IconButton>
          )}
          {room &&
            (room.numOpenTickets ?? 0) > 0 &&
            (!showTickets ? (
              <IconButton
                onClick={() => setForm({ showTickets: true })}
                sx={{ color: ({ palette }) => palette.error.main }}
              >
                <Warning />
              </IconButton>
            ) : (
              <IconButton
                onClick={() => setForm({ showTickets: false })}
                sx={{ color: ({ palette }) => palette.text.primary }}
              >
                <Close />
              </IconButton>
            ))}
          <Grid
            sx={{
              py: "1rem",
            }}
          >
            <Grid
              container
              direction="column"
              sx={{ width: "100%", height: "100%", gap: "0.5rem" }}
            >
              {(room?.capacity ?? 0) > 0 && (
                <InformationLabel icon={<Groups />} label={room?.capacity} />
              )}
              {room?.vip && <VIP />}
            </Grid>
          </Grid>
        </Grid>
        {editMode && (
          <Grid
            sx={{
              padding: "1rem",
            }}
          >
            <CustomRoomForm
              name={name}
              address={address}
              extension={extension}
              protocol={protocol}
              setForm={setForm}
            >
              <Button
                loading={saving}
                disabled={!isValidForm}
                color="white"
                variant="contained"
                onClick={() => handleOnClickSave()}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                color="greyed"
                onClick={() =>
                  setForm({
                    editMode: false,
                    name: customRoom?.name,
                    address: customRoom?.address,
                    extension: customRoom?.extension,
                    protocol: customRoom?.protocol,
                  })
                }
              >
                Cancel
              </Button>
            </CustomRoomForm>
          </Grid>
        )}
        {showTickets &&
          (tickets.length === 0 ? (
            <LinearProgress />
          ) : (
            <Grid
              container
              sx={{
                width: "100%",
                justifyContent: "center",
                maxHeight: "100%",
                overflow: "auto",
              }}
            >
              <Grid container sx={{ width: "100%", padding: 1, gap: 1 }}>
                {tickets
                  .slice(
                    0,
                    tickets.length > 3 && showAllTickets ? undefined : 3
                  )
                  .map((ticket) => (
                    <TicketCard key={ticket.id} ticket={ticket} />
                  ))}
              </Grid>
              {tickets.length > 3 && (
                <Button
                  variant="text"
                  onClick={() => setShowAllTickets(!showAllTickets)}
                >
                  {showAllTickets ? t("...See less") : t("...See more")}
                </Button>
              )}
            </Grid>
          ))}
      </Grid>
      <IconButton
        sx={(theme) => ({
          borderRadius: "unset",
          backgroundColor: selected
            ? theme.palette.meeting.darker
            : theme.palette.meeting.background,
          color: theme.palette.text.primary,
          borderTopRightRadius: theme.shape.borderRadius,
          borderBottomRightRadius: theme.shape.borderRadius,
        })}
        onClick={() => handleOnClick()}
      >
        {selected ? <Remove /> : <Add />}
      </IconButton>
    </Grid>
  );
};

export default RoomCard;
